import styles from './Style/LayerOverview.module.scss';
import {UploadOutlined} from '@ant-design/icons';
import {Button, Divider, Modal, Tooltip} from 'antd';
import React, {useContext} from 'react';
import Upload from 'antd/lib/upload';
import {HuePicker} from 'react-color';
import LoadingContext from '../../../context/loadingContext/LoadingContext';
import IconPoint from '../../../assets/icon/locate2.png'
import {useActiveMarker, useMarkerStore} from "../../../store/markerStore";
import {withLoading} from "../../common/Loading/withLoading";
import {isEmpty} from "underscore";
import {usePointLayerControl} from "./hooks/usePointLayerControl";
import {useLayerControls} from "./hooks/useLayerControl";
import {useSurfaceMap} from "./hooks/useSurfaceMapControl";
import {useShapefileUpload} from "./hooks/useShapeFileUpload";
import UploadedLayers from "./component/Display/UploadedFile";
import ShapeFileLayers from "./component/Display/ShapeFile";
import ToggleItem from "./component/Display/ToogleItem";
import MenuSection from "./component/Display/LegendView";
import SurfaceMapRadioGroup from "./component/Display/SurfaceMapRadio";

export interface LayerOverviewProps {
  onMeshControl: (value: string) => any;
  result: any;
  setResult: any;
  legendStartEndValues: [number | null, number | null],
  setLegendStartEndValues: Function
}

export default function LayerOverview(props: LayerOverviewProps) {
  const activeMarker = useActiveMarker();
  const loadingContext = useContext(LoadingContext);
  const point = activeMarker

  const {
    fileList,
    uploadProps,
    uploadedFeatures,
    visibleUploads,
    uploadingShapefile,
    currentLayerColor,

    setCurrentLayerColor,
    handleOnUpload,
    onChangeUploadVisibility,
    deleteUpload,
    onCloseColorPickerDialog,
    applyColor,
    changeColor
  } = useShapefileUpload()

  const {
    showPointSection,
    setShowPointsSection,
    showLabelLayer,
    showFWEPoints,
    showInSituPoints,
    isHavePoint,
    isHaveFwePoint,
    isHaveInsituPoint,
    isHave2DSpectra,
    updateShowLabels,
    setShowFWEPoints,
    setShowInSituPoints
  } = usePointLayerControl()

  const {
    showLayerSection,
    setShowLayerSection,
    showDatasetZones,
    setShowDatasetZones,
    showGlobalShapeFile,
    setShowGlobalShapeFile,
    showMesh,
    updateMeshSwitch,
    updateShowShape,
  } = useLayerControls(props.onMeshControl, activeMarker, !!isHavePoint, !!isHave2DSpectra)

  const {
    showSurfaceMapSection,
    setShowSurfaceMapSection,
    handleSurfaceMap
  } = useSurfaceMap(props.result, props.setResult, props.legendStartEndValues, point)

  return (
    <div className={`${styles.root}`}>
      <Modal
        open={!isEmpty(currentLayerColor)}
        title="Select a color"
        onCancel={onCloseColorPickerDialog}
        footer={<Button onClick={onCloseColorPickerDialog}>OK</Button>}
        style={{display: 'flex', justifyContent: 'center'}}
      >
        <HuePicker
          color={currentLayerColor}
          onChange={(color) => setCurrentLayerColor(color.hex)}
          onChangeComplete={(color) => applyColor(color)}
        />
      </Modal>

      <MenuSection
        title="Points"
        isExpanded={showPointSection}
        onToggle={() => setShowPointsSection(!showPointSection)}
      >
        <ToggleItem
          label="User Points"
          icon={<img src={IconPoint} alt={''} width={30} height={30} style={{position: 'relative', right: '6px'}}/>}
          labelStyle={{position: 'relative', right: '7px'}}
          disabled={!isHavePoint}
          isActive={showLabelLayer}
          onToggle={() => updateShowLabels(!showLabelLayer)}
        />

        <ToggleItem
          label="In-Situ Station"
          iconStyle={styles.circleInSitu}
          disabled={!isHaveInsituPoint}
          isActive={showInSituPoints}
          onToggle={() => setShowInSituPoints(!showInSituPoints)}
        />

        <ToggleItem
          label="Local Model"
          iconStyle={styles.circle}
          disabled={!isHaveFwePoint}
          isActive={showFWEPoints}
          onToggle={() => setShowFWEPoints(!showFWEPoints)}
        />
      </MenuSection>

      <MenuSection
        title="Layers"
        isExpanded={showLayerSection}
        onToggle={() => setShowLayerSection(!showLayerSection)}
      >
        <ToggleItem
          label="Model Domains"
          iconStyle={styles.boxPerimeter}
          isActive={showDatasetZones}
          onToggle={() => setShowDatasetZones(!showDatasetZones)}
        />

        <ToggleItem
          label="Model Grid"
          iconStyle={styles.boxGrid}
          disabled={!isHavePoint}
          isActive={showMesh}
          onToggle={() => withLoading(() =>
              updateMeshSwitch(!showMesh),
            loadingContext,
            {source: 'meshSwitch'}
          )}
        />

        {point && (
          <ShapeFileLayers
            shapeFiles={point.shapeFiles}
            dataset={point.dataset}
            updateShowShape={updateShowShape}
            showDisabledMessage={point.id !== undefined && !isHave2DSpectra}
          />
        )}

        <ToggleItem
          label="Offshore Wind Farms"
          iconStyle={styles.boxOffshore}
          disabled={false}
          isActive={showGlobalShapeFile}
          onToggle={() => setShowGlobalShapeFile(!showGlobalShapeFile)}
        />

        <Divider style={{margin: 0}}/>

        <UploadedLayers
          uploadedFeatures={uploadedFeatures}
          visibleUploads={visibleUploads}
          onChangeUploadVisibility={onChangeUploadVisibility}
          deleteUpload={deleteUpload}
          changeColor={changeColor}
        />

        <div className={styles.rowUpload}>
          <Upload
            {...uploadProps}
            className={styles.upload}
            fileList={fileList}
          >
            <Tooltip title="The following files must be uploaded together: .shp, .shx, .prj">
              <Button
                type="primary"
                className={styles.uploadButton}
                loading={uploadingShapefile}
                onClick={handleOnUpload}
                size="small"
              >
                + Add Shape Files
                <UploadOutlined/>
              </Button>
            </Tooltip>
          </Upload>
        </div>
      </MenuSection>

      {point?.id && (
        <MenuSection
          title="Surface Map"
          isExpanded={showSurfaceMapSection}
          onToggle={() => setShowSurfaceMapSection(!showSurfaceMapSection)}
        >
          <div style={{overflowY: 'auto', maxHeight: '350px'}}>
            <SurfaceMapRadioGroup
              categories={point.surfaceMaps.categories}
              overlays={point.surfaceMaps.overlays}
              selectedValue={
                point.surfaceMaps.selectedLevel2
                  ? `${point.surfaceMaps.selectedLevel1}|${point.surfaceMaps.selectedLevel2}`
                  : 'none'
              }
              onChange={(value) => handleSurfaceMap(value, false)}
            />
          </div>
        </MenuSection>
      )}
    </div>);
}
